// passiveAssetMovement.js
// Derive FOUND -> LOST detection intervals from beacon reads, and pull the
// detecting reader's actual GPS track for each interval so we can show the
// beacon-tagged asset moving WITH the reader.
//
// A beacon has no GPS of its own; it is detected by a GPS reader device that
// reports the reader's lat/long. status === 'FOUND' means the beacon entered the
// reader's range; 'LOST' means it left. Between the two, the asset rode with the
// reader, so the reader's GPS track is the asset's movement.

import { fetchIotDeviceDataInPeriod } from './fuelService';
import { extractRawRows, normalizeRawPoints } from './utilizationEngine';

const HOUR_MS = 60 * 60 * 1000;

const statusOf = (read) =>
  String((read && read.status) || '').trim().toUpperCase();

// Thresholds for treating a detection as "towed" (moved with the reader) rather
// than just detected at a point in time.
export const TOW_MIN_DURATION_MS = 2 * 60 * 1000; // present for at least 2 min
export const TOW_MIN_DISTANCE_M = 50;             // reader moved at least 50 m meanwhile
export const TOW_NEAR_RSSI = -80;                 // and the tag was close (strong signal)

const haversineMeters = (a, b) => {
  if (!a || !b) return 0;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad((b.lat || 0) - (a.lat || 0));
  const dLng = toRad((b.lng || 0) - (a.lng || 0));
  const lat1 = toRad(a.lat || 0);
  const lat2 = toRad(b.lat || 0);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

// Decide whether an asset was TOWED during its FOUND→LOST interval: it must be
// present long enough AND the reader's GPS must have moved significantly while
// it was on board. `readerPoints` are the reader's (sanitized) GPS track points.
export function classifyIntervalTowing(interval, readerPoints, opts = {}) {
  const minDur = opts.minDurationMs != null ? opts.minDurationMs : TOW_MIN_DURATION_MS;
  const minDist = opts.minDistanceM != null ? opts.minDistanceM : TOW_MIN_DISTANCE_M;
  const nearRssi = opts.nearRssi != null ? opts.nearRssi : TOW_NEAR_RSSI;
  const start = interval.startTs;
  const end = interval.endTs;
  const durationMs = Math.max(0, end - start);

  const pts = (readerPoints || []).filter((p) => p.ts >= start && p.ts <= end);
  let distanceM = 0;
  for (let i = 1; i < pts.length; i += 1) {
    distanceM += haversineMeters(pts[i - 1], pts[i]);
  }

  // Proximity: the tag must have been CLOSE to the reader (strong signal) — a
  // weak signal means it was merely detected at range while the reader roamed,
  // not physically carried. Use the strongest of FOUND/LOST RSSI.
  const r1 = interval.startRead ? Number(interval.startRead.rssi) : NaN;
  const r2 = interval.endRead ? Number(interval.endRead.rssi) : NaN;
  const hasRssi = Number.isFinite(r1) || Number.isFinite(r2);
  const bestRssi = Math.max(Number.isFinite(r1) ? r1 : -Infinity, Number.isFinite(r2) ? r2 : -Infinity);
  const near = !hasRssi || bestRssi >= nearRssi;

  const towed = durationMs >= minDur && distanceM >= minDist && near;
  return { towed, distanceM, durationMs, near, bestRssi: hasRssi ? bestRssi : null, samples: pts.length };
}

// Stronger BLE RSSI is the value closer to 0 (less negative). Returns the
// "better" of two reads by signal.
const strongerRssi = (a, b) => {
  const ra = Number(a && a.rssi);
  const rb = Number(b && b.rssi);
  if (!Number.isFinite(ra)) return b;
  if (!Number.isFinite(rb)) return a;
  return ra >= rb ? a : b;
};

// Club beacon reads that share the same reader + scan time into one cluster, so
// we can see every BLE asset a reader detected at the same moment (each with its
// own signal strength). `bucketMs` tolerates tiny per-beacon timestamp jitter
// within one scan cycle.
export function buildScanClusters(beaconReads, bucketMs = 1000) {
  const map = new Map();
  (beaconReads || []).forEach((r) => {
    if (!r || !r.eventTime) return;
    const lat = Number(r.latitude);
    const lng = Number(r.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (Math.abs(lat) < ZERO_EPS && Math.abs(lng) < ZERO_EPS) return; // skip null island

    const bucket = Math.round(r.eventTime / bucketMs);
    const key = `${r.readerImei || '-'}|${bucket}`;
    if (!map.has(key)) {
      map.set(key, { key, readerImei: r.readerImei, ts: r.eventTime, lat, lng, byAsset: new Map() });
    }
    const c = map.get(key);
    if (r.eventTime < c.ts) c.ts = r.eventTime;

    const aKey = r.assetIdentifier || '-';
    const det = {
      assetIdentifier: r.assetIdentifier,
      rssi: r.rssi,
      status: r.status,
      ts: r.eventTime,
      raw: r.raw
    };
    const existing = c.byAsset.get(aKey);
    // Keep the strongest-signal read per asset within the scan.
    c.byAsset.set(aKey, existing ? strongerRssi(existing, det) : det);
  });

  const clusters = Array.from(map.values()).map((c) => {
    const detections = Array.from(c.byAsset.values())
      .sort((a, b) => Number(b.rssi) - Number(a.rssi)); // strongest first
    return {
      key: c.key,
      readerImei: c.readerImei,
      ts: c.ts,
      lat: c.lat,
      lng: c.lng,
      detections,
      count: detections.length
    };
  });
  clusters.sort((a, b) => a.ts - b.ts);
  return clusters;
}

// Occupancy-based clustering: at each reader position (scan time) show EVERY asset
// whose FOUND→LOST interval is active then — i.e. everything that moved WITH the
// reader, not just assets that happened to emit a read at that exact timestamp.
// Open intervals (FOUND with no LOST) run to `rangeEndMs`.
export function buildOccupancyClusters(beaconReads, rangeEndMs, bucketMs = 1000) {
  const reads = (beaconReads || []).filter((r) => {
    if (!r || !r.eventTime) return false;
    const lat = Number(r.latitude);
    const lng = Number(r.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (Math.abs(lat) < ZERO_EPS && Math.abs(lng) < ZERO_EPS) return false; // null island
    return true;
  });

  const { intervals } = buildDetectionIntervals(beaconReads, rangeEndMs);

  // Per-asset reads (sorted) so we can show the signal strength as of each scan.
  const readsByAsset = new Map();
  reads.forEach((r) => {
    const a = r.assetIdentifier || '-';
    if (!readsByAsset.has(a)) readsByAsset.set(a, []);
    readsByAsset.get(a).push(r);
  });
  readsByAsset.forEach((list) => list.sort((x, y) => x.eventTime - y.eventTime));

  const latestReadAtOrBefore = (assetId, ts) => {
    const list = readsByAsset.get(assetId || '-') || [];
    let best = null;
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].eventTime <= ts) best = list[i];
      else break;
    }
    return best;
  };

  // Distinct reader scan positions (bucketed), each with the reader's coordinates.
  const scanMap = new Map();
  reads.forEach((r) => {
    const bucket = Math.round(r.eventTime / bucketMs);
    const key = `${r.readerImei || '-'}|${bucket}`;
    if (!scanMap.has(key)) {
      scanMap.set(key, { key, readerImei: r.readerImei, ts: r.eventTime, lat: Number(r.latitude), lng: Number(r.longitude) });
    } else {
      const s = scanMap.get(key);
      if (r.eventTime < s.ts) s.ts = r.eventTime;
    }
  });

  const clusters = Array.from(scanMap.values()).map((s) => {
    const occupants = intervals.filter(
      (itv) => itv.readerImei === s.readerImei && itv.startTs <= s.ts && s.ts <= itv.endTs
    );
    const detections = occupants
      .map((itv) => {
        const read = latestReadAtOrBefore(itv.assetIdentifier, s.ts) || itv.startRead;
        return {
          assetIdentifier: itv.assetIdentifier,
          rssi: read ? read.rssi : null,
          status: 'ON BOARD',
          open: itv.open,
          ts: s.ts,
          raw: read ? read.raw : null
        };
      })
      .sort((a, b) => Number(b.rssi) - Number(a.rssi));
    return {
      key: s.key,
      readerImei: s.readerImei,
      ts: s.ts,
      lat: s.lat,
      lng: s.lng,
      detections,
      count: detections.length
    };
  });

  clusters.sort((a, b) => a.ts - b.ts);
  return clusters;
}

// Per-asset detection summary for a reader: how long each asset stayed with the
// reader (towed) vs a brief pass-by. Uses FOUND→LOST intervals when available,
// else the span of raw reads. `towMinMs` is the minimum sustained presence to
// be considered "towed".
export function summarizeReaderDetections(beaconReads, rangeEndMs, towMinMs = 5 * 60 * 1000) {
  const { intervals } = buildDetectionIntervals(beaconReads, rangeEndMs);
  const spanByAsset = new Map();

  // Prefer FOUND→LOST interval spans.
  intervals.forEach((itv) => {
    const a = itv.assetIdentifier || '-';
    const span = Math.max(0, (itv.endTs || 0) - (itv.startTs || 0));
    const cur = spanByAsset.get(a) || { assetIdentifier: a, spanMs: 0, scans: 0, firstTs: itv.startTs, lastTs: itv.endTs };
    cur.spanMs = Math.max(cur.spanMs, span);
    cur.firstTs = Math.min(cur.firstTs, itv.startTs);
    cur.lastTs = Math.max(cur.lastTs, itv.endTs);
    spanByAsset.set(a, cur);
  });

  // Count scans per asset from raw reads (and cover assets without clean intervals).
  (beaconReads || []).forEach((r) => {
    if (!r || !r.eventTime) return;
    const a = r.assetIdentifier || '-';
    const cur = spanByAsset.get(a) || { assetIdentifier: a, spanMs: 0, scans: 0, firstTs: r.eventTime, lastTs: r.eventTime };
    cur.scans += 1;
    cur.firstTs = Math.min(cur.firstTs, r.eventTime);
    cur.lastTs = Math.max(cur.lastTs, r.eventTime);
    if (cur.spanMs === 0) cur.spanMs = Math.max(0, cur.lastTs - cur.firstTs);
    spanByAsset.set(a, cur);
  });

  const assets = Array.from(spanByAsset.values()).map((x) => ({
    ...x,
    towed: x.spanMs >= towMinMs
  }));
  assets.sort((a, b) => b.spanMs - a.spanMs);

  return {
    distinctAssets: assets.length,
    towed: assets.filter((a) => a.towed),
    passBy: assets.filter((a) => !a.towed),
    assets
  };
}

// A telemetry point with lat/lng at (0,0) (or within a tiny epsilon) means the
// GPS had no fix — "null island". We must not plot it.
const ZERO_EPS = 1e-6;
const isNoFix = (p) =>
  !p ||
  p.lat === null || p.lat === undefined ||
  p.lng === null || p.lng === undefined ||
  (Math.abs(Number(p.lat)) < ZERO_EPS && Math.abs(Number(p.lng)) < ZERO_EPS);

// Replace no-fix points with the last valid coordinate and flag them `noGps`,
// so the path holds at the last known position and the UI can indicate the
// signal gap. Leading no-fix points (before any valid fix) are dropped because
// we have no position to show for them.
export function sanitizeGpsPoints(points) {
  const out = [];
  let lastValid = null;
  (points || []).forEach((p) => {
    if (isNoFix(p)) {
      if (lastValid) {
        out.push({ ...p, lat: lastValid.lat, lng: lastValid.lng, noGps: true });
      }
      // else: no prior fix yet — skip until we get one
    } else {
      lastValid = { lat: Number(p.lat), lng: Number(p.lng) };
      out.push({ ...p, lat: lastValid.lat, lng: lastValid.lng, noGps: false });
    }
  });
  return out;
}

// Pair FOUND/LOST events per (beacon asset, reader) into time intervals.
// `rangeEndMs` closes a still-open FOUND (no later LOST in range).
export function buildDetectionIntervals(beaconReads, rangeEndMs) {
  const groups = new Map();
  (beaconReads || []).forEach((r) => {
    if (!r || !r.eventTime) return;
    const st = statusOf(r);
    if (st !== 'FOUND' && st !== 'LOST') return; // only FOUND/LOST drive intervals
    const key = `${r.assetIdentifier || '-'}__${r.readerImei || '-'}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });

  const intervals = [];
  const anomalies = [];

  groups.forEach((reads, key) => {
    const sorted = reads.slice().sort((a, b) => (a.eventTime || 0) - (b.eventTime || 0));
    let open = null;

    sorted.forEach((r) => {
      const st = statusOf(r);
      if (st === 'FOUND') {
        // Ignore duplicate FOUND while one is already open (keep earliest).
        if (!open) open = r;
      } else if (st === 'LOST') {
        if (open) {
          intervals.push({
            key,
            assetIdentifier: open.assetIdentifier,
            readerImei: open.readerImei,
            startTs: open.eventTime,
            endTs: r.eventTime,
            open: false,
            startRead: open,
            endRead: r
          });
          open = null;
        } else {
          anomalies.push({ key, type: 'LOST_WITHOUT_FOUND', read: r });
        }
      }
    });

    if (open) {
      intervals.push({
        key,
        assetIdentifier: open.assetIdentifier,
        readerImei: open.readerImei,
        startTs: open.eventTime,
        endTs: rangeEndMs || open.eventTime,
        open: true,
        startRead: open,
        endRead: null
      });
    }
  });

  intervals.sort((a, b) => (a.startTs || 0) - (b.startTs || 0));
  return { intervals, anomalies };
}

// Split a [start,end] window into sub-windows no larger than maxHours, because
// the in-period endpoint rejects windows > 48h.
export function chunkWindow(startTs, endTs, maxHours = 48) {
  const chunks = [];
  if (!(endTs > startTs)) return chunks;
  const maxMs = maxHours * HOUR_MS;
  let s = startTs;
  while (s < endTs) {
    const e = Math.min(s + maxMs, endTs);
    chunks.push({ start: s, end: e });
    s = e;
  }
  return chunks;
}

// Fetch + normalize the reader GPS track for one interval. deviceId is the
// reader IMEI; assetId is the reader's host equipment id (required by the API).
export async function fetchReaderTrack({ readerImei, assetId, startTs, endTs }) {
  if (!readerImei) return { success: false, points: [], error: 'Missing reader IMEI' };
  if (!assetId) return { success: false, points: [], error: 'Reader is not linked to an asset' };
  if (!(endTs > startTs)) return { success: false, points: [], error: 'Invalid time window' };

  const chunks = chunkWindow(startTs, endTs, 48);
  const all = [];
  let lastError = null;

  for (const { start, end } of chunks) {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchIotDeviceDataInPeriod(readerImei, start, end, assetId);
    if (res && res.success) {
      const pts = normalizeRawPoints(extractRawRows(res.data));
      all.push(...pts);
    } else {
      lastError = (res && res.error) || 'Failed to fetch device data';
    }
  }

  if (all.length === 0) {
    return { success: false, points: [], error: lastError || 'No GPS points returned' };
  }

  // Sort by ts and dedupe identical timestamps.
  all.sort((a, b) => a.ts - b.ts);
  const deduped = [];
  let prevTs = null;
  for (const p of all) {
    if (p.ts !== prevTs) {
      deduped.push(p);
      prevTs = p.ts;
    }
  }

  // Replace (0,0) GPS-loss points with the last known fix, flagged noGps.
  const cleaned = sanitizeGpsPoints(deduped);
  if (cleaned.length === 0) {
    return { success: false, points: [], error: lastError || 'No valid GPS fixes in window' };
  }

  return { success: true, points: cleaned, error: lastError };
}
